/**
 * Reseller Service API Contracts
 * Core types for the partner portal: auth, dashboard, bookings, commissions,
 * API keys, webhooks, balance, deposits.
 */

import { z } from 'zod';

// ============================================================================
// Auth
// ============================================================================

export const ResellerLoginRequestSchema = z.object({
  client_id: z.string(),
  client_secret: z.string(),
});
export type ResellerLoginRequest = z.infer<typeof ResellerLoginRequestSchema>;

export const ResellerPartnerTierSchema = z.enum(['BRONZE', 'SILVER', 'GOLD']);
export type ResellerPartnerTier = z.infer<typeof ResellerPartnerTierSchema>;

export const ResellerLoginResponseSchema = z.object({
  access_token: z.string(),
  refresh_token: z.string().optional(),
  expires_in: z.number().int(),
  token_type: z.string(),
  partner: z.object({
    id: z.string(),
    partnerCode: z.string(),
    name: z.string(),
    email: z.string().email(),
    tier: ResellerPartnerTierSchema,
  }),
});
export type ResellerLoginResponse = z.infer<typeof ResellerLoginResponseSchema>;

// ============================================================================
// Dashboard
// ============================================================================

export const RevenueTimeSeriesPointSchema = z.object({
  date: z.string(),
  revenue: z.number(),
});
export type RevenueTimeSeriesPoint = z.infer<typeof RevenueTimeSeriesPointSchema>;

export const BookingsByTicketTypeSchema = z.object({
  ticketType: z.string(),
  count: z.number().int(),
});
export type BookingsByTicketType = z.infer<typeof BookingsByTicketTypeSchema>;

export const TierProgressSchema = z.object({
  currentTier: ResellerPartnerTierSchema,
  nextTier: z.enum(['SILVER', 'GOLD']).nullable(),
  currentRevenue: z.number(),
  nextTierThreshold: z.number().nullable(),
  progressPercent: z.number(),
  revenueNeeded: z.number().nullable(),
});
export type TierProgress = z.infer<typeof TierProgressSchema>;

export const QuotaUtilizationSnapshotSchema = z.object({
  allocated: z.number().int(),
  used: z.number().int(),
  remaining: z.number().int(),
  utilizationPercent: z.number(),
});
export type QuotaUtilizationSnapshot = z.infer<typeof QuotaUtilizationSnapshotSchema>;

export const QuotaUtilizationSchema = z.object({
  daily: QuotaUtilizationSnapshotSchema.nullable(),
  monthly: QuotaUtilizationSnapshotSchema.nullable(),
});
export type QuotaUtilization = z.infer<typeof QuotaUtilizationSchema>;

export const UpcomingSettlementSchema = z.object({
  settlementId: z.string(),
  periodEnd: z.string(),
  totalAmount: z.number(),
  status: z.enum(['DRAFT', 'APPROVED', 'PAID']),
});
export type UpcomingSettlement = z.infer<typeof UpcomingSettlementSchema>;

export const PartnerDashboardMetricsSchema = z.object({
  todayBookingsCount: z.number().int(),
  mtdRevenue: z.number(),
  mtdCommission: z.number(),
  apiCallCount: z.number().int(),
  todayBookingsChange: z.number().optional(),
  revenueTimeSeries: z.array(RevenueTimeSeriesPointSchema).optional(),
  bookingsByTicketType: z.array(BookingsByTicketTypeSchema).optional(),
  tierProgress: TierProgressSchema.optional(),
  quotaUtilization: QuotaUtilizationSchema.optional(),
  upcomingSettlement: UpcomingSettlementSchema.nullable().optional(),
});
export type PartnerDashboardMetrics = z.infer<typeof PartnerDashboardMetricsSchema>;

// ============================================================================
// Balance & Ledger (P3-DEPOSIT)
// ============================================================================

export const LedgerEntryTypeSchema = z.enum([
  'DEPOSIT',
  'BOOKING_DEBIT',
  'REFUND_CREDIT',
  'ADJUSTMENT',
]);
export type LedgerEntryType = z.infer<typeof LedgerEntryTypeSchema>;

export const LedgerEntrySchema = z.object({
  id: z.string(),
  type: LedgerEntryTypeSchema,
  amount: z.number(),
  balanceAfter: z.number(),
  referenceId: z.string().nullable(),
  referenceType: z.string().nullable(),
  description: z.string().nullable(),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
});
export type LedgerEntry = z.infer<typeof LedgerEntrySchema>;

export const PartnerBalanceSchema = z.object({
  currentBalance: z.number(),
  maxDiscountPercent: z.number(),
  minDepositForDiscount: z.number(),
  recentTransactions: z.array(LedgerEntrySchema),
});
export type PartnerBalance = z.infer<typeof PartnerBalanceSchema>;

export const PartnerDepositStatusSchema = z.enum([
  'PENDING',
  'CONFIRMED',
  'EXPIRED',
  'FAILED',
]);
export type PartnerDepositStatus = z.infer<typeof PartnerDepositStatusSchema>;

export const PartnerDepositRecordSchema = z.object({
  id: z.string(),
  partnerId: z.string(),
  amount: z.number(),
  status: PartnerDepositStatusSchema,
  paymentGateway: z.string(),
  paymentChannel: z.string(),
  externalId: z.string(),
  vaNumber: z.string().nullable(),
  resolvedMethod: z.string().nullable(),
  expiresAt: z.string().nullable(),
  paidAt: z.string().nullable(),
  createdAt: z.string(),
});
export type PartnerDepositRecord = z.infer<typeof PartnerDepositRecordSchema>;

export const CreateDepositRequestSchema = z.object({
  amount: z.number().positive(),
});
export type CreateDepositRequest = z.infer<typeof CreateDepositRequestSchema>;

export const CreateDepositResponseSchema = z.object({
  depositId: z.string(),
  paymentId: z.string(),
  gatewayProvider: z.string(),
  snapToken: z.string().optional(),
  clientKey: z.string().optional(),
  redirectUrl: z.string().optional(),
  qrCodeUrl: z.string().optional(),
  amount: z.number(),
  expiresAt: z.string(),
  status: z.string(),
});
export type CreateDepositResponse = z.infer<typeof CreateDepositResponseSchema>;

// ============================================================================
// Bookings (reseller portal view)
// ============================================================================

export const ResellerBookingStatusSchema = z.enum([
  'CONFIRMED',
  'CANCELLED',
  'PENDING',
  'SUBMITTED_FOR_REVIEW',
  'USED',
  'EXPIRED',
]);
export type ResellerBookingStatus = z.infer<typeof ResellerBookingStatusSchema>;

export const ResellerBookingSchema = z.object({
  bookingId: z.string(),
  bookingRef: z.string(),
  customerName: z.string(),
  ticketType: z.string(),
  visitDate: z.string(),
  createdAt: z.string(),
  status: ResellerBookingStatusSchema,
  totalAmount: z.number(),
  commissionAmount: z.number(),
  quantity: z.number().int(),
});
export type ResellerBooking = z.infer<typeof ResellerBookingSchema>;

export const ResellerBookingsListResponseSchema = z.object({
  data: z.array(ResellerBookingSchema),
  meta: z.object({
    page: z.number().int(),
    limit: z.number().int(),
    total: z.number().int(),
    totalPages: z.number().int(),
  }),
});
export type ResellerBookingsListResponse = z.infer<typeof ResellerBookingsListResponseSchema>;

export const ResellerBookingsParamsSchema = z.object({
  page: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().positive().max(100).optional(),
  status: z.string().optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
});
export type ResellerBookingsParams = z.infer<typeof ResellerBookingsParamsSchema>;

// ============================================================================
// Commission Reports
// ============================================================================

export const CommissionLineItemSchema = z.object({
  bookingRef: z.string(),
  visitDate: z.string(),
  ticketType: z.string(),
  quantity: z.number().int(),
  totalAmount: z.number(),
  commissionRate: z.number(),
  commissionAmount: z.number(),
});
export type CommissionLineItem = z.infer<typeof CommissionLineItemSchema>;

export const CommissionReportSchema = z.object({
  period: z.string(),
  totalRevenue: z.number(),
  totalCommission: z.number(),
  commissionRate: z.number(),
  bookingCount: z.number().int(),
  items: z.array(CommissionLineItemSchema),
});
export type CommissionReport = z.infer<typeof CommissionReportSchema>;

// ============================================================================
// API Keys
// ============================================================================

export const ApiKeySchema = z.object({
  keyId: z.string(),
  maskedKey: z.string(),
  label: z.string(),
  createdAt: z.string(),
  lastUsedAt: z.string().nullable(),
  isActive: z.boolean(),
});
export type ApiKey = z.infer<typeof ApiKeySchema>;

export const RotateApiKeyResponseSchema = z.object({
  keyId: z.string(),
  newKey: z.string(),
  maskedKey: z.string(),
});
export type RotateApiKeyResponse = z.infer<typeof RotateApiKeyResponseSchema>;

// ============================================================================
// Webhook Logs
// ============================================================================

export const WebhookLogSchema = z.object({
  logId: z.string(),
  eventType: z.string(),
  targetUrl: z.string(),
  status: z.enum(['SUCCESS', 'FAILED']),
  httpStatusCode: z.number().int().nullable(),
  retryCount: z.number().int(),
  lastAttemptAt: z.string(),
  payload: z.string(),
});
export type WebhookLog = z.infer<typeof WebhookLogSchema>;

export const WebhookLogsResponseSchema = z.object({
  logs: z.array(WebhookLogSchema),
  total: z.number().int(),
  page: z.number().int(),
  totalPages: z.number().int(),
});
export type WebhookLogsResponse = z.infer<typeof WebhookLogsResponseSchema>;

// ============================================================================
// API Usage
// ============================================================================

export const ApiUsageEndpointSchema = z.object({
  endpoint: z.string(),
  requestCount: z.number().int(),
  errorCount: z.number().int(),
  avgResponseTimeMs: z.number(),
  errorRate: z.number(),
});
export type ApiUsageEndpoint = z.infer<typeof ApiUsageEndpointSchema>;

export const ApiUsageResponseSchema = z.object({
  period: z.string(),
  totalRequests: z.number().int(),
  totalErrors: z.number().int(),
  overallErrorRate: z.number(),
  overallAvgResponseTimeMs: z.number(),
  byEndpoint: z.array(ApiUsageEndpointSchema),
});
export type ApiUsageResponse = z.infer<typeof ApiUsageResponseSchema>;

export * from './extras';
export * from './groups';
export * from './admin';
